/**
 * Module dependencies.
 */

import { Me } from './use-me';
import { cookieStorage } from 'app/core/utils/cookie-storage';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';

/**
 * Export `ProfilePayload` type.
 */

export type ProfilePayload = Partial<Pick<Me, 'firstName' | 'lastName' | 'profilePhotoUrl'>>;

/**
 * Export `useUpdateProfile`.
 *
 * Patches the current user (`PATCH /me`). The endpoint saves silently (no
 * global toast) so per-field auto-save stays quiet. On success we refresh the
 * `me` cache and keep the `user` cookie (read by the sidebar badge) in sync.
 */

export function useUpdateProfile() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ProfilePayload): Promise<Me> =>
      client.patch(resolveAppUrl('updateProfile'), payload).then(({ data }: { data: any }) => data?.data),
    onSuccess: user => {
      queryClient.setQueryData(['me'], user);

      const current = (cookieStorage.get('user') as any) ?? {};

      // Match the domain the server set the cookie with (host), otherwise a
      // host-only cookie is created alongside the server's and the stale one
      // wins on read (so the landing badge would not refresh).
      cookieStorage.set('user', { ...current, ...user }, { domain: window.location.hostname, path: '/' });
    }
  });
}
